import React, { useEffect, useState } from "react"
import { Flex, Box, Radio, Button, Text, FormField } from "@indeed/ifl-components"
import { BrowseJobsButton } from "../../common/components/browse-jobs-button"
import { toastSuccess } from "../../common/components/indeed-toast-container"
import { ResponsiveButtonGroup } from "../../common/components/responsive-button-group"
import { useI18n } from "../../common/utils/i18n-context"
import { useAppDispatch, useAppSelector } from "../../common/utils/redux-helpers"
import { ReqStatus } from "../../common/utils/utils"
import { submitUnpause, submitPause } from "./pause-api"
import { AdvancedPauseDuration, PauseDurationUnits, PausePageStatus, PauseSubmissionStatus } from "./pause-model"
import {
    pauseSubmissionStatusSelector,
    isPausedSelector,
    pausePageStatusSelector,
    pauseDataSelector,
    durationUnitsAsString,
} from "./pause-slice"

export interface PauseTimePickerProps {
    encryptedInfo: string
}

export enum PauseTimeOption {
    THIRTY_DAYS = "30_days",
    THREE_MONTHS = "3_months",
    SIX_MONTHS = "6_months",
    DONT_PAUSE = "dont_pause",
}

const displayTimeOption = (duration: number, durationUnits: PauseDurationUnits, currentPauseStatus: AdvancedPauseDuration): boolean => {
    return currentPauseStatus && (currentPauseStatus.duration !== duration || currentPauseStatus.durationUnits !== durationUnits)
}

export const PauseTimePicker: React.FC<PauseTimePickerProps> = ({ encryptedInfo }) => {
    const dispatch = useAppDispatch()
    const submissionStatus = useAppSelector(pauseSubmissionStatusSelector)
    const [selected, setSelected] = useState<PauseTimeOption>()
    const isPaused = useAppSelector(isPausedSelector)
    const pausePageStatus = useAppSelector(pausePageStatusSelector)
    const currentPauseStatus = useAppSelector(pauseDataSelector)
    const { gettext } = useI18n()

    const timeOptions: { [key in PauseTimeOption]: AdvancedPauseDuration & { label: string; display: boolean } } = {
        [PauseTimeOption.THIRTY_DAYS]: {
            duration: 30,
            durationUnits: PauseDurationUnits.DAYS,
            label: `30 ${durationUnitsAsString({ duration: 30, durationUnits: PauseDurationUnits.DAYS })}`,
            display: displayTimeOption(30, PauseDurationUnits.DAYS, currentPauseStatus),
        },
        [PauseTimeOption.THREE_MONTHS]: {
            duration: 3,
            durationUnits: PauseDurationUnits.MONTHS,
            label: `3 ${durationUnitsAsString({ duration: 3, durationUnits: PauseDurationUnits.MONTHS })}`,
            display: displayTimeOption(3, PauseDurationUnits.MONTHS, currentPauseStatus),
        },
        [PauseTimeOption.SIX_MONTHS]: {
            duration: 6,
            durationUnits: PauseDurationUnits.MONTHS,
            label: `6 ${durationUnitsAsString({ duration: 6, durationUnits: PauseDurationUnits.MONTHS })}`,
            display: displayTimeOption(6, PauseDurationUnits.MONTHS, currentPauseStatus),
        },
        [PauseTimeOption.DONT_PAUSE]: {
            duration: 0,
            durationUnits: PauseDurationUnits.MONTHS,
            label: gettext("Don't pause"),
            display: isPaused,
        },
    }

    useEffect(() => {
        if (pausePageStatus === PausePageStatus.PAUSE_SUCCESS || pausePageStatus === PausePageStatus.UNPAUSE_SUCCESS) {
            setSelected(undefined)
        }
    }, [pausePageStatus, submissionStatus])

    const saveOnClick = async () => {
        if (selected) {
            const { duration, durationUnits } = timeOptions[selected]
            const res =
                selected === PauseTimeOption.DONT_PAUSE
                    ? await dispatch(submitUnpause({ encryptedInfo, gettext })).unwrap()
                    : await dispatch(
                          submitPause({
                              encryptedInfo,
                              selectedDuration: {
                                  duration,
                                  durationUnits,
                              },
                              gettext,
                          })
                      ).unwrap()
            if (res === ReqStatus.SUCCESS) toastSuccess(gettext("Pause changes saved"))
        }
    }

    return (
        <Flex
            sx={{
                gap: 2,
                flexDirection: "column",
                inlineSize: "100%",
            }}
        >
            <FormField
                as="fieldset"
                label={gettext("Change pause length (optional)")}
                sx={{ fontWeight: "bold", color: "foreground.strong", marginBlockStart: 6, marginBlockEnd: 0 }}
            >
                <Box>
                    {Object.entries(timeOptions)
                        .filter(([_, value]) => value.display)
                        .map(([key, value]) => (
                            <Radio
                                key={key}
                                name="pause-time-options"
                                id={key}
                                checked={key === selected}
                                onChange={() => {
                                    setSelected(key as PauseTimeOption)
                                }}
                            >
                                <Text level={3} sx={{ fontWeight: "normal" }} variant="contrast">
                                    {value.label}
                                </Text>
                            </Radio>
                        ))}
                </Box>
            </FormField>
            <ResponsiveButtonGroup sx={{ marginBlockStart: 3 }}>
                <Button
                    onClick={saveOnClick}
                    disabled={!selected}
                    loading={submissionStatus === PauseSubmissionStatus.SUBMITTING}
                    type="button"
                >
                    {gettext("Save")}
                </Button>
                <BrowseJobsButton />
            </ResponsiveButtonGroup>
        </Flex>
    )
}
